import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useEffect, useState } from "react";
import { FacilityType } from "../../types";

interface FacilityTypeFormProps {
  isVisible: boolean;
  mode: "add" | "edit";
  formData: { name: string; description: string };
  onFormChange: (data: { name: string; description: string }) => void;
  onSave: (data: any) => void;
  onCancel: () => void;
  editingFacility?: FacilityType;
}

export function FacilityTypeForm({
  isVisible,
  mode,
  formData,
  onFormChange,
  onSave,
  onCancel,
  editingFacility,
}: FacilityTypeFormProps) {
  const [localFormData, setLocalFormData] = useState({
    name: formData.name || "",
    description: formData.description || "",
  });

  // Update local form data when props change
  useEffect(() => {
    setLocalFormData({
      name: formData.name || "",
      description: formData.description || "",
    });
  }, [formData]);

  // Form field definitions
  const formFields: FormField[] = [
    {
      name: "name",
      type: "text",
      label: "Facility Type Name",
      placeholder: "Enter facility type name",
      required: true,
      className: "col-span-1 mt-3",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description",
      placeholder: "Enter facility type description",
      rows: 3,
      required: false,
      className: "col-span-1 mt-3",
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: localFormData,
  });

  // Reset form when localFormData changes
  useEffect(() => {
    form.reset(localFormData);
  }, [localFormData, form]);

  // Handle form submission
  const handleSubmit = (data: any) => {
    if (!data.name.trim()) {
      return;
    }
    onSave(data);
  };

  // Handle form reset/cancel
  const handleReset = () => {
    onCancel();
  };

  if (!isVisible) return null;

  return (
    <div className="space-y-4">
      <div className="space-y-6">
        <DynamicForm
          fields={formFields}
          form={form}
          onSubmit={handleSubmit}
          onReset={handleReset}
          gridColumns={1}
          responsiveColumns={{
            sm: 1,
            md: 1,
          }}
          config={{
            showResetButton: true,
            showSubmitButton: true,
            submitButtonText:
              mode === "edit" ? "Update Facility Type" : "Add Facility Type",
            resetButtonText: "Cancel",
            spacing: "comfortable",
            classes: {
              form: "space-y-6",
              button:
                "px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2",
              buttonContainer: "flex gap-2 justify-end pt-6 mt-8",
              input:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
              textarea:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
              select:
                "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
              label: "text-sm font-medium mb-2 block",
              error: "text-sm text-red-600 mt-1",
              container: "space-y-1",
            },
          }}
        />
      </div>
    </div>
  );
}
